import { ComponentStyleProps } from "../../../lib/styles/props/component-style-props";
import React from "react";
import {
  Box,
  Button,
  ComponentMultiStyleConfig,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import { FileUploadItemContent } from "../../file-upload/types";
import { MdSend } from "react-icons/md";
import { CMFormInputBaseFileAttachableTextArea } from "../../control";

export type CMCommentThreadInputProps = {
  /**
   * プレースホルダー
   */
  placeholder?: string;

  /**
   * コメント本文
   */
  comment?: string;

  /**
   * 添付ファイル
   */
  files: FileUploadItemContent[];

  /**
   * ファイルアップロードボタンのラベル
   */
  buttonUploadLabel: string;

  /**
   * ファイルアップロード中であることを通知するラベル
   */
  uploadingLabel?: string;

  /**
   * 送信ボタンのラベル
   */
  buttonSubmitLabel: string;

  /**
   * コメント変更イベント
   * @param comment コメント内容
   */
  onChangeComment?: (comment: string) => void;

  /**
   * ファイルが追加されたときに、その追加ファイルを引数として呼び出される。
   */
  onAddFile: (file: File) => Promise<void>;

  /**
   * ファイルが削除されたときに、その削除ファイルを引数として呼び出される。
   */
  onDeleteFile: (fileId: string) => void;

  /**
   * ファイルが選択されたときに、そのファイルを引数として呼び出される。
   * @param fileId
   * @param fileName ファイル名
   */
  onDownloadFile?: (fileId: string, fileName: string) => void;

  /**
   * 送信ボタンイベント
   */
  onSubmit: () => void;
} & ComponentStyleProps;

/**
 * 質疑応答の返信入力コンポーネント
 * コメントの返信とファイルの添付が行える
 */
export const CMCommentThreadInput = ({
  placeholder,
  comment,
  files,
  buttonUploadLabel,
  uploadingLabel,
  buttonSubmitLabel,
  onChangeComment,
  onAddFile,
  onDeleteFile,
  onDownloadFile,
  onSubmit,

  sx,
  ...rest
}: CMCommentThreadInputProps) => {
  const styles = useMultiStyleConfig("CMCommentThreadInputStyles");

  return (
    <Box sx={{ ...styles.container, ...sx }} {...rest}>
      <CMFormInputBaseFileAttachableTextArea
        files={files}
        buttonUploadLabel={buttonUploadLabel}
        uploadingLabel={uploadingLabel}
        placeholder={placeholder}
        value={comment}
        onAddFile={onAddFile}
        onDeleteFile={onDeleteFile}
        onDownloadFile={onDownloadFile}
        onChange={onChangeComment}
      />

      {/*送信ボタン*/}
      <Button
        colorScheme={"teal"}
        leftIcon={<MdSend />}
        onClick={onSubmit}
        sx={styles.submitButton}
      >
        {buttonSubmitLabel}
      </Button>
    </Box>
  );
};

export const CMCommentThreadInputStyles: ComponentMultiStyleConfig = {
  parts: [
    "container",
    "formArea",
    "commentArea",
    "fileArea",
    "fileUploadedArea",
    "file",
    "uploadButton",
    "buttonContents",
    "buttonIcon",
    "buttonLabel",
    "submitButton",
  ],
  baseStyle: {
    container: {
      display: "flex",
      flexDirection: "column",
    },

    formArea: {
      overflow: "hidden",
      border: "solid 2px",
      borderColor: "gray.300",
      borderRadius: "lg",
      _focusWithin: {
        borderColor: "blue.500",
      },
    },

    commentArea: {
      w: "100%",
      minH: "100px",
      px: "4",
      py: "2",

      borderBottomRightRadius: "0px",
      borderBottomLeftRadius: "0px",
      border: "none",
      outline: "none",
      boxShadow: "none",
      color: "gray.700",
    },
    commentAreaOption: {
      _focus: {
        boxShadow: "none",
      },
    },
    fileArea: {
      border: "none",
      outline: "none",
      boxShadow: "none",
      bgColor: "gray.100",
    },
    fileUploadedArea: {
      bgColor: "gray.100",
    },
    file: {
      position: "relative",
      left: "20px",
      paddingTop: "4px",
    },
    uploadButton: {
      h: "32px",
      w: "100%",
      borderTopRightRadius: "0px",
      borderTopLeftRadius: "0px",
      position: "relative",
      marginTop: "2px",
      paddingTop: "6px",
      bgColor: "gray.100",
    },
    uploadButtonContents: {
      display: "flex",
      flexDirection: "row",
      position: "absolute",
      left: "24px",
      color: "blackAlpha.500",
    },
    uploadButtonIcon: {},
    uploadButtonLabel: {
      h: "20px",
      marginLeft: "5px",
      fontWeight: "400",
      fontSize: "14px",
      fontStyle: "normal",
      fontFamily: "'Inter'",
      color: "blackAlpha.500",
    },

    submitButton: {
      display: "block",
      h: "32px",
      w: "fit-content",
      position: "relative",
      margin: "auto",
      marginTop: "8px",
    },
  },
};
